import {
  ENDPOINT_RSI_LOGOUT,
  ENDPOINT_USER_TOKEN,
  ROOT_URL,
} from 'constants/constants';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useRef,
  useContext,
  useMemo,
  useState,
} from 'react';
import qs from 'qs';
import { getAPI, createLocalAuthHeaders, logout } from 'utils/commonService';

export interface LocalAuthHeaders {
  'x-amzn-oidc-accesstoken': string;
  'x-amzn-oidc-data': string;
}

export interface LoginState {
  token: string;
  roles: string[];
}

export interface LoginFunction {
  fetchToken: () => Promise<void>;
  setLocalToken: (token: string) => void;
  logoutCaaS: (needMoveLogoutPage?: boolean) => void;
  getLocalAuthHeader: () => Promise<LocalAuthHeaders>;
}

const initialState: LoginState = {
  token: '',
  roles: [],
};

const initialFunction: LoginFunction = {
  fetchToken: async () => {},
  setLocalToken: (__token: string) => {},
  logoutCaaS: (__needMoveLogoutPage?: boolean) => {},
  getLocalAuthHeader: async () => {
    return {
      'x-amzn-oidc-accesstoken': '',
      'x-amzn-oidc-data': '',
    };
  },
};

export const LoginContext = createContext<LoginState & LoginFunction>({
  ...initialState,
  ...initialFunction,
});

LoginContext.displayName = 'LoginContext';

interface LoginContextProviderProps {
  children: ReactNode;
}

export const LoginContextProvider: React.FC<LoginContextProviderProps> = ({
  children,
}: LoginContextProviderProps) => {
  const [token, setToken] = useState<string>('');
  const [roles, setRoles] = useState<string[]>([]);
  const [localAuthHeaders, setLocalAuthHeaders] = useState<LocalAuthHeaders>();
  const controllerRef = useRef<AbortController>();

  const getLocalAuthHeader = useCallback(async () => {
    if (localAuthHeaders) return localAuthHeaders;
    // ローカル実行の場合は認証ヘッダーを取得して返却
    const headers = await createLocalAuthHeaders(token);
    setLocalAuthHeaders(headers);
    return headers;
  }, [localAuthHeaders, token]);

  const handleFetchToken = useCallback(async () => {
    try {
      if (controllerRef.current) controllerRef.current.abort();
      controllerRef.current = new AbortController();

      const tokenResponse = await getAPI<LoginState>(ENDPOINT_USER_TOKEN, {
        ...initialFunction,
        getLocalAuthHeader,
      });
      setToken(tokenResponse.token);
      setRoles(tokenResponse.roles);
      controllerRef.current = undefined;
    } catch (e: any) {
      // Abortされた場合は空配列を返却して処理終了
      if (e.message === 'canceled') {
        return;
      }
      throw e;
    }
  }, [getLocalAuthHeader]);

  const setLocalToken = useCallback((token: string) => {
    setToken(token);
  }, []);

  const logoutCaaS = useCallback(async (needMoveLogoutPage: boolean = true) => {
    await logout();
    setToken('');
    const query = qs.stringify({
      url: ROOT_URL,
    });
    // ログアウト画面へ遷移が必要になる場合
    if (needMoveLogoutPage)
      window.location.assign(`${ENDPOINT_RSI_LOGOUT}?${query}`);
  }, []);

  const contextValue = useMemo(() => {
    return {
      token,
      roles,
      fetchToken: handleFetchToken,
      setLocalToken,
      logoutCaaS,
      getLocalAuthHeader,
    };
  }, [
    token,
    roles,
    handleFetchToken,
    setLocalToken,
    logoutCaaS,
    getLocalAuthHeader,
  ]);

  return (
    <LoginContext.Provider value={contextValue}>
      {children}
    </LoginContext.Provider>
  );
};

export const useLoginContext = () => {
  return useContext(LoginContext);
};
